import type { ChatImage } from '@/components/layout/chat-pane.types'

/**
 * Client-side caps for chat image paste/drop and /api/chat-stream JSON body.
 * Stay under Express 5MB express.json limit with headroom for history + metadata.
 */
export const CHAT_IMAGE_BUDGET = {
  /** Max pasted/dropped images in one outbound message. */
  maxImagesPerMessage: 5,
  /** Per-image base64 string length cap (bytes). */
  maxBytesPerImage: 4 * 1024 * 1024,
  /** Combined base64 bytes for images staged in the composer. */
  maxTotalStagedBytes: 4 * 1024 * 1024,
  /** Preflight cap on JSON.stringify(chat-stream body) before fetch. */
  maxRequestPayloadBytes: 4 * 1024 * 1024,
} as const

export type ImageBudgetViolation =
  | 'too_many_images'
  | 'image_too_large'
  | 'total_staged_too_large'
  | 'payload_too_large'

export const CHAT_IMAGE_BUDGET_TOAST_TITLE = 'Cannot attach images'
export const CHAT_PAYLOAD_TOAST_TITLE = 'This chat turn is too large to send'

export function imageBase64Bytes(img: ChatImage): number {
  return img.base64?.length ?? 0
}

export function totalImageBytes(images: ChatImage[]): number {
  return images.reduce((sum, img) => sum + imageBase64Bytes(img), 0)
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function formatImageBudgetMeter(images: ChatImage[]): string {
  const count = images.length
  const total = totalImageBytes(images)
  const max = CHAT_IMAGE_BUDGET.maxTotalStagedBytes
  return `${count} image${count === 1 ? '' : 's'} - ${formatBytes(total)} / ${formatBytes(max)}`
}

/** API body uses base64 + mediaType only; dataUrl is display-only and must not be sent (doubles payload). */
export function toChatStreamImagePayload(
  img: ChatImage,
): Pick<ChatImage, 'base64' | 'mediaType'> {
  return { base64: img.base64, mediaType: img.mediaType }
}

export function validateStagedImages(
  currentImages: ChatImage[],
  incomingImages: ChatImage[],
): { ok: true } | { ok: false; reason: ImageBudgetViolation; message: string } {
  const combined = [...currentImages, ...incomingImages]

  if (combined.length > CHAT_IMAGE_BUDGET.maxImagesPerMessage) {
    const max = CHAT_IMAGE_BUDGET.maxImagesPerMessage
    const over = combined.length - max
    const removeLabel = over === 1 ? '1 image' : `${over} images`
    return {
      ok: false,
      reason: 'too_many_images',
      message: `Limit is ${max} images per message. Remove ${removeLabel} to add more, or send extras in a new message.`,
    }
  }

  for (const img of incomingImages) {
    const bytes = imageBase64Bytes(img)
    if (bytes > CHAT_IMAGE_BUDGET.maxBytesPerImage) {
      const max = formatBytes(CHAT_IMAGE_BUDGET.maxBytesPerImage)
      return {
        ok: false,
        reason: 'image_too_large',
        message: `This image is too large (${formatBytes(bytes)}). Max ${max} per image - compress or resize first.`,
      }
    }
  }

  const total = totalImageBytes(combined)
  if (total > CHAT_IMAGE_BUDGET.maxTotalStagedBytes) {
    const max = formatBytes(CHAT_IMAGE_BUDGET.maxTotalStagedBytes)
    return {
      ok: false,
      reason: 'total_staged_too_large',
      message: `Attachments total ${formatBytes(total)} (max ${max}). Remove an image or use smaller screenshots.`,
    }
  }

  return { ok: true }
}

export function validateStagedImagesOnly(
  images: ChatImage[],
): ReturnType<typeof validateStagedImages> {
  if (images.length === 0) return { ok: true }
  return validateStagedImages([], images)
}

export function validateChatStreamPayload(
  payload: unknown,
): { ok: true; bytes: number } | { ok: false; reason: 'payload_too_large'; message: string; bytes: number } {
  let serialized: string
  try {
    serialized = JSON.stringify(payload)
  } catch {
    return {
      ok: false,
      reason: 'payload_too_large',
      message: 'This chat turn is too large to send. A screenshot can be well under the per-image cap while history plus the image still exceed 4 MB. Start a new chat, or remove images.',
      bytes: Number.POSITIVE_INFINITY,
    }
  }

  const bytes = new TextEncoder().encode(serialized).length
  if (bytes > CHAT_IMAGE_BUDGET.maxRequestPayloadBytes) {
    return {
      ok: false,
      reason: 'payload_too_large',
      message: `This chat turn is too large (${formatBytes(bytes)}, max ${formatBytes(CHAT_IMAGE_BUDGET.maxRequestPayloadBytes)}). A screenshot can look small while the whole request (history plus images) is over the cap. Start a new chat, or remove images.`,
      bytes,
    }
  }

  return { ok: true, bytes }
}
